/**
 * Ficha de uma estação meteorológica (INMET) ou de um aeródromo (METAR).
 *
 * Compacta, para caber em telas baixas e no celular
 * (docs/proposta-ficha-tempo-compacta.md):
 *
 * - cabeçalho com a observação que vale no quadro exibido — a última até ele,
 *   com a hora dela, porque é a hora que impede de ler a temperatura das 15:00
 *   como se fosse a das 17:00 — e uma faixa de chips (vento, orvalho, pressão,
 *   chuva, visibilidade);
 * - as 48 h num meteograma: faixas empilhadas sobre UM eixo de tempo, com um
 *   cursor que marca a mesma hora em todas; o valor da hora fica à direita do
 *   título de cada faixa e a hora, no topo. Sem cursor, vale o quadro do
 *   player. Os desenhos ficam parados; o que anda com o player é a marca.
 *
 * No METAR, o tempo presente vem em português e o texto bruto logo abaixo
 * (recolhível): é ali que o aeródromo diz "FU" — fumaça — medido por gente.
 */
import { CloudRain, Droplets, ExternalLink, Eye, Gauge, Wind, type LucideIcon } from 'lucide-react';
import { useEffect, useMemo, useState, type ReactNode } from 'react';
import { formatLonLat } from '../lib/format';
import { formatDateBrt, formatDayTimeBrt, formatTimeBrt, formatUtcBesideBrt } from '../lib/time';
import { shownTimeOf, useTimeStore } from '../state/time';
import { EixoTempo, GRAFICO_A, GraficoTempo } from '../ui/GraficoTempo';
import { centroPct } from '../ui/fatias';
import { corDe as corChuva } from '../stations/series';
import { urlDaFonte } from './api';
import { comUnidade, corDe, numero } from './escalas';
import { COR_SEM_DADO, type MeteoFeature } from './layer';
import { DEFASADA_MS, faixa, indiceNoQuadro, kmh, rajadasFortes, rumo, serieDe, somaChuva, trechos, ultimaAte } from './leitura';
import { maiuscula, tempoPresente, temFumaca, visibilidadeTexto } from './metar';
import { useMeteoStore } from './store';
import type { Chave, Dados, Estacao, Leitura } from './types';
import { ProximosDiasAqui } from '../previsao/ProximosDiasAqui';

const COR_T = '#ff8f5a';
const COR_TD = '#6fdcb0';
const COR_UR = '#6fb2e0';
const COR_VV = '#d4ec7a';
const COR_RAJ = '#ffd25a';
const COR_P = '#c7b8ff';

/** Altura (px) de cada faixa do meteograma: a temperatura, principal, é a maior. */
const ALTURA = { t: 36, ur: 24, vento: 28, p: 22, chuva: 24 } as const;

/** Cursor do meteograma: o mesmo em todas as faixas. */
interface Cursor {
  sob: number | null;
  aoSob: (i: number | null) => void;
}

interface Linha {
  serie: readonly (number | null)[];
  cor: string;
  /** Rajada: pontos, e não linha — é um pico dentro da hora, não uma média. */
  pontos?: boolean;
}

/** Faixa de linhas do meteograma, com uma ou mais séries na mesma escala. */
function Linhas(props: {
  linhas: Linha[];
  instantes: readonly number[];
  iAtual: number;
  piso: number;
  fixo?: [number, number];
  rotulo: string;
  altura: number;
  cursor: Cursor;
}) {
  const { linhas, instantes, piso } = props;
  const n = instantes.length;
  const f = useMemo(() => {
    if (props.fixo) return { min: props.fixo[0], max: props.fixo[1], base: props.fixo[0], amplitude: props.fixo[1] - props.fixo[0] };
    return faixa(
      linhas.map((l) => l.serie),
      piso,
    );
  }, [linhas, piso, props.fixo]);
  const partes = useMemo(() => linhas.map((l) => trechos(l.serie)), [linhas]);
  if (!f) return <div className="text-[11px] text-ink-400">Sem medição nas últimas 48 h.</div>;

  const A = GRAFICO_A;
  const FOLGA = 2.5;
  const y = (v: number) => A - FOLGA - ((v - f.base) / f.amplitude) * (A - 2 * FOLGA);

  return (
    <GraficoTempo
      instantes={instantes}
      iAtual={props.iAtual}
      rotulo={props.rotulo}
      valor={() => null}
      resumo={null}
      soDesenho
      altura={props.altura}
      sob={props.cursor.sob}
      aoSob={props.cursor.aoSob}
      desenho={({ w, sob }) => (
        <>
          {sob !== null && (
            <line x1={(sob + 0.5) * w} y1="0" x2={(sob + 0.5) * w} y2={A} stroke="rgba(255,255,255,0.35)" strokeWidth="1" vectorEffect="non-scaling-stroke" />
          )}
          {linhas.map((l, k) =>
            l.pontos
              ? null
              : partes[k]!.map((t) =>
                  t.length === 1 ? (
                    <line
                      key={`${k}-${t[0]}`}
                      x1={(t[0]! + 0.2) * w}
                      x2={(t[0]! + 0.8) * w}
                      y1={y(l.serie[t[0]!]!)}
                      y2={y(l.serie[t[0]!]!)}
                      stroke={l.cor}
                      strokeWidth="1.5"
                      strokeLinecap="round"
                      vectorEffect="non-scaling-stroke"
                    />
                  ) : (
                    <polyline
                      key={`${k}-${t[0]}`}
                      points={t.map((i) => `${((i + 0.5) * w).toFixed(2)},${y(l.serie[i]!).toFixed(2)}`).join(' ')}
                      fill="none"
                      stroke={l.cor}
                      strokeWidth="1.5"
                      strokeLinejoin="round"
                      strokeLinecap="round"
                      vectorEffect="non-scaling-stroke"
                    />
                  ),
                ),
          )}
        </>
      )}
      sobre={({ destaque }) => (
        <>
          {/* Pontos em HTML: o `preserveAspectRatio="none"` faria deles elipses. */}
          {linhas.map((l, k) =>
            l.pontos
              ? l.serie.map((v, i) =>
                  typeof v === 'number' ? (
                    <span
                      key={`p${k}-${i}`}
                      aria-hidden
                      className="pointer-events-none absolute size-[4px] -translate-x-1/2 -translate-y-1/2 rounded-full"
                      style={{ left: `${centroPct(i, n)}%`, top: `${(y(v) / A) * 100}%`, background: l.cor, opacity: i === destaque ? 1 : 0.85 }}
                    />
                  ) : null,
                )
              : null,
          )}
          {destaque !== null &&
            linhas.map((l, k) => {
              const v = l.serie[destaque];
              if (typeof v !== 'number' || l.pontos) return null;
              return (
                <span
                  key={`d${k}`}
                  aria-hidden
                  className="pointer-events-none absolute size-[7px] -translate-x-1/2 -translate-y-1/2 rounded-full ring-2 ring-ink-900"
                  style={{ left: `${centroPct(destaque, n)}%`, top: `${(y(v) / A) * 100}%`, background: l.cor }}
                />
              );
            })}
        </>
      )}
    />
  );
}

/** Chuva hora a hora, em barras (as cores dos pluviômetros), sobre uma linha de base. */
function Barras(props: { serie: readonly (number | null)[]; instantes: readonly number[]; iAtual: number; rotulo: string; cursor: Cursor }) {
  const { serie, instantes } = props;
  const medidos = serie.filter((v): v is number => typeof v === 'number');
  const maior = Math.max(0.2, ...medidos);
  const A = GRAFICO_A;
  const TOCO = 1.4;
  return (
    <GraficoTempo
      instantes={instantes}
      iAtual={props.iAtual}
      rotulo={props.rotulo}
      valor={() => null}
      resumo={null}
      soDesenho
      altura={ALTURA.chuva}
      sob={props.cursor.sob}
      aoSob={props.cursor.aoSob}
      desenho={({ w, destaque }) => (
        <>
          {serie.map((v, i) =>
            // Hora medida: base visível (zero é medição); sem medição, nada.
            typeof v === 'number' ? (
              <rect key={`b${i}`} x={i * w} y={A - 0.8} width={Math.max(0.6, w - 0.4)} height={0.8} fill="currentColor" opacity={0.35} />
            ) : null,
          )}
          {serie.map((v, i) => {
            if (typeof v !== 'number' || v <= 0) return null;
            const h = Math.max(2, (A - TOCO - 1) * (v / maior) + TOCO);
            return <rect key={i} x={i * w} y={A - h} width={Math.max(0.6, w - 0.4)} height={h} fill={corChuva(v)} opacity={i === destaque ? 1 : 0.85} />;
          })}
        </>
      )}
    />
  );
}

/**
 * Setinhas da direção do vento, de 6 em 6 horas (para onde o vento vai), numa
 * linha própria sob a faixa: por cima dela, brigavam com os pontos da rajada.
 */
function SetasDirecao({ dv }: { dv: readonly (number | null)[] }) {
  const n = dv.length;
  return (
    <div aria-hidden className="pointer-events-none relative h-2.5">
      {dv.map((g, i) =>
        typeof g === 'number' && i % 6 === 3 ? (
          <span
            key={i}
            className="absolute top-0 text-[10px] leading-none text-ink-200"
            style={{ left: `${centroPct(i, n)}%`, transform: `translateX(-50%) rotate(${(g + 180) % 360}deg)` }}
          >
            ↑
          </span>
        ) : null,
      )}
    </div>
  );
}

/** Uma faixa do meteograma: título, escala (mín–máx de 48 h) e o valor da hora do cursor à direita. */
function Faixa({ titulo, escala, leitura, children }: { titulo: ReactNode; escala?: string | null; leitura: ReactNode; children: ReactNode }) {
  return (
    <div className="pt-1">
      <div className="flex items-baseline gap-1.5 text-[10px] leading-tight text-ink-400">
        <span className="min-w-0 truncate">{titulo}</span>
        {escala && <span className="shrink-0 tabular-nums">{escala}</span>}
        <span className="ml-auto shrink-0 text-xs leading-tight font-semibold whitespace-nowrap text-ink-100 tabular-nums">{leitura}</span>
      </div>
      {children}
    </div>
  );
}

/** "24–36" com o formato da faixa, sobre todas as séries dela; `null` sem medição. */
function minMax(serie: readonly (number | null)[], formato: (v: number) => string, ...outras: (readonly (number | null)[])[]): string | null {
  const m = [serie, ...outras].flat().filter((v): v is number => typeof v === 'number');
  if (m.length === 0) return null;
  const lo = formato(Math.min(...m));
  const hi = formato(Math.max(...m));
  return lo === hi ? lo : `${lo}–${hi}`;
}

/** Valor da hora do cursor, na cor da série; "—" quando a hora não tem medição (nunca o último valor conhecido). */
function Valor({ v, formato, cor }: { v: number | null | undefined; formato: (v: number) => string; cor?: string }) {
  return typeof v === 'number' ? <span style={{ color: cor }}>{formato(v)}</span> : <span className="text-ink-400">—</span>;
}

/**
 * Quadro do cabeçalho: ícone e valor, com uma linha curta embaixo quando
 * importa (o rumo do vento). O nome e o detalhe completo aparecem ao passar o
 * mouse, no foco do teclado ou num toque — no celular não há "passar o
 * mouse", e o que só existisse no hover sumiria dali. O balão abre para
 * baixo: para cima, tapava o valor do quadro de cima e a hora da observação.
 */
function Quadro(props: {
  icone: LucideIcon;
  rotulo: string;
  valor: string;
  curto?: string;
  detalhe?: string;
  aberto: boolean;
  alternar: () => void;
  /** Coluna da direita: o balão abre para a esquerda, para não vazar da ficha. */
  direita?: boolean;
}) {
  const Icone = props.icone;
  const texto = props.detalhe ? `${props.rotulo}: ${props.detalhe}` : props.rotulo;
  return (
    <button
      type="button"
      data-quadro=""
      aria-label={`${props.rotulo}: ${props.valor}${props.detalhe ? `, ${props.detalhe}` : ''}`}
      aria-expanded={props.aberto}
      onClick={props.alternar}
      className="group relative min-w-0 rounded-lg bg-white/5 px-1.5 py-1 text-left hover:bg-white/10 focus-visible:outline-2 focus-visible:outline-mata-400"
    >
      <span className="flex items-baseline gap-1">
        <Icone size={13} className="shrink-0 self-center text-ink-400" aria-hidden />
        <span className="shrink-0 text-xs font-semibold whitespace-nowrap text-ink-100 tabular-nums">{props.valor}</span>
        {props.curto && <span className="min-w-0 truncate text-[10px] text-ink-300 tabular-nums">{props.curto}</span>}
      </span>
      <span
        role="tooltip"
        className={`pointer-events-none absolute top-full z-10 mt-1 w-max max-w-[13rem] rounded-md border border-ink-600 bg-ink-800 px-2 py-1 text-[11px] leading-snug text-ink-100 shadow-lg ${
          props.direita ? 'right-0' : 'left-0'
        } ${props.aberto ? 'block' : 'hidden group-hover:block group-focus-visible:block'}`}
      >
        {texto}
      </span>
    </button>
  );
}

/** Ícone do símbolo do mapa: quadrado (INMET) ou losango (METAR). */
export function SimboloEstacao({ rede, cor, tamanho = 10 }: { rede: Estacao['rede']; cor: string | null; tamanho?: number }) {
  const r = tamanho / 2 - 1;
  return (
    <svg viewBox={`${-tamanho / 2} ${-tamanho / 2} ${tamanho} ${tamanho}`} width={tamanho} height={tamanho} aria-hidden className="shrink-0">
      {rede === 'm' ? (
        <polygon
          points={`0,${-r} ${r},0 0,${r} ${-r},0`}
          fill={cor ?? 'none'}
          stroke={cor ? 'rgba(8,13,24,0.9)' : COR_SEM_DADO}
          strokeWidth="1.1"
          strokeDasharray={cor ? undefined : '2 1.5'}
        />
      ) : (
        <rect
          x={-r * 0.8}
          y={-r * 0.8}
          width={r * 1.6}
          height={r * 1.6}
          fill={cor ?? 'none'}
          stroke={cor ? 'rgba(8,13,24,0.9)' : COR_SEM_DADO}
          strokeWidth="1.1"
          strokeDasharray={cor ? undefined : '2 1.5'}
        />
      )}
    </svg>
  );
}

function subtitulo(e: Estacao): string {
  const lugar = e.uf ? e.uf : e.pais && e.pais !== 'BR' ? e.pais : null;
  if (e.rede === 'm') return [`aeródromo ${e.cod}`, lugar, 'METAR (NOAA AWC)'].filter(Boolean).join(' · ');
  const tipo = /^A\d/.test(e.cod) ? `automática ${e.cod}` : e.oscar ? `sinótica ${e.cod} (OMM)` : `convencional ${e.cod}`;
  return [lugar, tipo, 'INMET'].filter(Boolean).join(' · ');
}

const grau = (x: number) => `${numero(x, 1)}°`;
const inteiro = (x: number) => `${Math.round(x)}`;

export function MeteoPopup({ dado }: { dado: MeteoFeature }) {
  // A ficha lê a estação da store, e não do clique: com dados novos e a ficha
  // aberta, a série nova tem de vir com o `t0` novo (o cuidado de rivers).
  const chave = `${dado.estacao.rede}:${dado.estacao.cod}`;
  const estacao = useMeteoStore((s) => s.dados.estacoes.find((e) => `${e.rede}:${e.cod}` === chave)) ?? dado.estacao;
  const t0 = useMeteoStore((s) => s.dados.t0);
  const passoMin = useMeteoStore((s) => s.dados.passoMin);
  const slots = useMeteoStore((s) => s.dados.slots);
  const instante = useTimeStore((s) => shownTimeOf(s));
  const [sob, setSob] = useState<number | null>(null);
  const cursor: Cursor = { sob, aoSob: setSob };
  // Dados novos deslocam a grade: o índice do cursor passaria a ser outra hora.
  useEffect(() => setSob(null), [t0, slots]);
  // Tocar fora das faixas — na ficha, no mapa ou no player — devolve o cursor ao quadro.
  useEffect(() => {
    if (sob === null) return;
    const fora = (e: PointerEvent) => {
      if (!(e.target instanceof Element && e.target.closest('[data-grafico-tempo]'))) setSob(null);
    };
    document.addEventListener('pointerdown', fora, true);
    return () => document.removeEventListener('pointerdown', fora, true);
  }, [sob]);

  const grade: Pick<Dados, 't0' | 'passoMin' | 'slots'> = useMemo(() => ({ t0, passoMin, slots }), [t0, passoMin, slots]);
  const l: Leitura | null = useMemo(() => ultimaAte(estacao, grade, instante) ?? estacao.ultima, [estacao, grade, instante]);
  const instantes = useMemo(() => Array.from({ length: slots }, (_, i) => t0 + i * passoMin * 60_000), [t0, passoMin, slots]);
  const iAtual = indiceNoQuadro(slots, t0, passoMin, instante);
  const serie = useMemo(() => {
    const de = (k: Chave) => serieDe(estacao, k, t0, passoMin, slots);
    const vv = de('vv').map((v) => (v === null ? null : kmh(v)));
    const raj = de('raj').map((v) => (v === null ? null : kmh(v)));
    return { t: de('t'), td: de('td'), ur: de('ur'), vv, raj, rajForte: rajadasFortes(vv, raj), dv: de('dv'), p: de('p'), chuva: de('chuva') };
  }, [estacao, t0, passoMin, slots]);

  const v = l?.valores ?? {};
  const idade = l && instante !== null ? instante - l.ms : 0;
  const t = v.t;
  const cor = typeof t === 'number' ? corDe('t', t) : null;
  const fumaca = temFumaca(l?.wx);
  const frases = tempoPresente(l?.wx);
  const chuva24 = useMemo(() => {
    if (estacao.rede !== 'i' || !l) return null;
    const fim = Math.round((l.ms - t0) / (passoMin * 60_000));
    return somaChuva(serie.chuva.slice(Math.max(0, fim - 23), fim + 1));
  }, [estacao.rede, l, t0, passoMin, serie.chuva]);
  const total48 = somaChuva(serie.chuva);
  const temChuva = serie.chuva.some((x) => x !== null);
  const temPressao = serie.p.some((x) => x !== null);
  // A data só aparece quando a observação não é do dia do quadro.
  const mesmoDia = l && instante !== null && formatDateBrt(l.ms) === formatDateBrt(instante);

  // Quadros do cabeçalho, na ordem de importância; o balão de um só por vez.
  const [dica, setDica] = useState<string | null>(null);
  useEffect(() => {
    if (dica === null) return;
    const fora = (e: PointerEvent) => {
      if (!(e.target instanceof Element && e.target.closest('[data-quadro]'))) setDica(null);
    };
    document.addEventListener('pointerdown', fora, true);
    return () => document.removeEventListener('pointerdown', fora, true);
  }, [dica]);
  const quadros: Omit<Parameters<typeof Quadro>[0], 'aberto' | 'alternar'>[] = [];
  if (typeof v.vv === 'number') {
    const direcao = kmh(v.vv) < 2 ? 'calmaria' : rumo(v.dv) ? `de ${rumo(v.dv)} (${Math.round(v.dv!)}°)` : 'direção variável';
    const rajada = typeof v.raj === 'number' ? `rajada ${numero(Math.round(kmh(v.raj)))} km/h` : null;
    quadros.push({
      icone: Wind,
      rotulo: 'Vento',
      valor: comUnidade('vento', kmh(v.vv)),
      curto: [kmh(v.vv) < 2 ? 'calmaria' : (rumo(v.dv) ?? 'variável'), rajada && `raj. ${numero(Math.round(kmh(v.raj!)))}`].filter(Boolean).join(' · '),
      detalhe: [direcao, rajada].filter(Boolean).join(' · '),
    });
  }
  if (typeof v.chuva === 'number') {
    const em24 = chuva24 ? `${numero(chuva24.mm, 1)} mm em 24 h${chuva24.horas < 24 ? ` (${chuva24.horas} h medidas)` : ''}` : null;
    quadros.push({
      icone: CloudRain,
      rotulo: 'Chuva na hora',
      valor: comUnidade('chuva', v.chuva),
      curto: chuva24 ? `24 h: ${numero(chuva24.mm, 1)}` : undefined,
      detalhe: em24 ?? undefined,
    });
  }
  if (typeof v.td === 'number') quadros.push({ icone: Droplets, rotulo: 'Ponto de orvalho', valor: comUnidade('td', v.td), curto: 'orvalho' });
  if (typeof v.p === 'number')
    quadros.push({
      icone: Gauge,
      rotulo: 'Pressão ao nível do mar',
      valor: comUnidade('p', v.p),
      detalhe: typeof v.ps === 'number' ? `${numero(v.ps, 1)} hPa na estação` : undefined,
    });
  if (typeof v.vis === 'number') quadros.push({ icone: Eye, rotulo: 'Visibilidade', valor: visibilidadeTexto(v.vis) ?? '—' });

  // Hora em leitura no meteograma: a do cursor ou, sem ele, a do quadro.
  const destaque = sob ?? (iAtual >= 0 && iAtual < slots ? iAtual : null);
  const em = <T,>(s: readonly T[]): T | null => (destaque === null ? null : (s[destaque] ?? null));

  return (
    <div className="space-y-1.5">
      <div>
        <div className="flex items-center gap-1.5 leading-tight font-semibold text-white">
          <SimboloEstacao rede={estacao.rede} cor={cor} />
          {estacao.nome}
        </div>
        <div className="text-xs text-ink-300">{subtitulo(estacao)}</div>
      </div>

      {!l ? (
        <div className="text-sm text-ink-300">Sem observação nas últimas 48 h.</div>
      ) : (
        <div>
          <div className="flex items-baseline gap-2">
            {typeof t === 'number' ? (
              <span className="text-xl font-semibold tabular-nums" style={{ color: cor ?? undefined }}>
                {comUnidade('t', t)}
              </span>
            ) : (
              <span className="text-sm text-ink-300">temperatura não medida</span>
            )}
            {typeof v.ur === 'number' && <span className="text-xs text-ink-200 tabular-nums">umidade {comUnidade('ur', v.ur)}</span>}
          </div>
          <div className="text-[11px] text-ink-300 tabular-nums">
            observado {mesmoDia ? formatTimeBrt(l.ms) : formatDayTimeBrt(l.ms)} (Brasília) · {formatUtcBesideBrt(l.ms)}
            {l.speci && ' · SPECI'}
          </div>
          {idade > DEFASADA_MS && (
            <div className="text-[11px] text-alerta-300">
              {Math.floor(idade / 3_600_000)} h {Math.round((idade % 3_600_000) / 60_000)} min antes do quadro exibido
              {idade > 2 * 3_600_000 && ' — velha demais para o mapa, que mostra a estação vazada'}
            </div>
          )}
        </div>
      )}

      {l && quadros.length > 0 && (
        <div className="grid grid-cols-2 gap-1">
          {quadros.map((q, i) => (
            <Quadro key={q.rotulo} {...q} aberto={dica === q.rotulo} alternar={() => setDica(dica === q.rotulo ? null : q.rotulo)} direita={i % 2 === 1} />
          ))}
        </div>
      )}

      {estacao.rede === 'm' && l?.raw && (
        <div className={`rounded-lg px-2 py-1 ${fumaca ? 'border border-fogo-400/40 bg-fogo-400/10' : 'bg-white/5'}`}>
          {frases.length > 0 ? (
            <div className={`text-xs font-medium ${fumaca ? 'text-fogo-400' : 'text-ink-100'}`}>{maiuscula(frases.join('; '))}</div>
          ) : (
            <div className="text-[11px] text-ink-300">Sem fenômeno de tempo presente informado.</div>
          )}
          {/* Começa aberto quando o METAR fala de fumaça, névoa seca, trovoada ou nevoeiro: aí o
              texto bruto é a prova. Por METAR (key), para não desfazer o que o usuário abriu. */}
          <details key={l.raw} open={fumaca || /TS|FG/.test(l.wx ?? '') || undefined}>
            <summary className="cursor-pointer text-[10px] text-ink-400 select-none">METAR bruto</summary>
            <div className="font-mono text-[10px] leading-snug break-words text-ink-300">{l.raw}</div>
          </details>
        </div>
      )}

      <div className="text-ink-200">
        <div className="flex items-baseline justify-between gap-2 border-t border-white/10 pt-1.5 text-[10px] text-ink-400">
          <span>Últimas 48 h</span>
          <span aria-live={sob === null ? 'off' : 'polite'} className="rounded bg-white/10 px-1.5 text-[11px] text-ink-100 tabular-nums">
            {destaque === null ? 'toque ou passe o mouse' : `${sob === null ? 'no quadro · ' : ''}${formatDayTimeBrt(instantes[destaque]!)}`}
          </span>
        </div>

        <Faixa
          titulo={
            <>
              <span style={{ color: COR_T }}>Temperatura</span> · <span style={{ color: COR_TD }}>orvalho</span> °C
            </>
          }
          escala={minMax(serie.t, (x) => numero(x, 0), serie.td)}
          leitura={
            <>
              <Valor v={em(serie.t)} formato={grau} cor={COR_T} /> / <Valor v={em(serie.td)} formato={grau} cor={COR_TD} />
            </>
          }
        >
          <Linhas
            linhas={[
              { serie: serie.t, cor: COR_T },
              { serie: serie.td, cor: COR_TD },
            ]}
            instantes={instantes}
            iAtual={iAtual}
            piso={4}
            altura={ALTURA.t}
            cursor={cursor}
            rotulo={`Temperatura e ponto de orvalho nas últimas 48 horas${minMax(serie.t, grau) ? `, temperatura de ${minMax(serie.t, grau)}` : ''}`}
          />
        </Faixa>

        <Faixa
          titulo={<span style={{ color: COR_UR }}>Umidade %</span>}
          escala={minMax(serie.ur, inteiro)}
          leitura={<Valor v={em(serie.ur)} formato={(x) => `${Math.round(x)}%`} cor={COR_UR} />}
        >
          <Linhas
            linhas={[{ serie: serie.ur, cor: COR_UR }]}
            instantes={instantes}
            iAtual={iAtual}
            piso={10}
            fixo={[0, 100]}
            altura={ALTURA.ur}
            cursor={cursor}
            rotulo="Umidade relativa nas últimas 48 horas"
          />
        </Faixa>

        <Faixa
          titulo={
            <>
              <span style={{ color: COR_VV }}>Vento</span> · <span style={{ color: COR_RAJ }}>rajada forte</span> km/h
            </>
          }
          escala={minMax(serie.vv, inteiro)}
          leitura={
            <>
              <Valor v={em(serie.vv)} formato={inteiro} cor={COR_VV} /> / <Valor v={em(serie.raj)} formato={inteiro} cor={COR_RAJ} />
            </>
          }
        >
          <Linhas
            linhas={[
              { serie: serie.vv, cor: COR_VV },
              { serie: serie.rajForte, cor: COR_RAJ, pontos: true },
            ]}
            instantes={instantes}
            iAtual={iAtual}
            piso={10}
            altura={ALTURA.vento}
            cursor={cursor}
            rotulo="Vento médio e rajadas fortes (10 km/h ou mais acima do vento) nas últimas 48 horas"
          />
          <SetasDirecao dv={serie.dv} />
        </Faixa>

        {temPressao && (
          <Faixa
            titulo={<span style={{ color: COR_P }}>Pressão (mar) hPa</span>}
            escala={minMax(serie.p, inteiro)}
            leitura={<Valor v={em(serie.p)} formato={(x) => numero(x, 1)} cor={COR_P} />}
          >
            <Linhas
              linhas={[{ serie: serie.p, cor: COR_P }]}
              instantes={instantes}
              iAtual={iAtual}
              piso={4}
              altura={ALTURA.p}
              cursor={cursor}
              rotulo="Pressão ao nível do mar nas últimas 48 horas"
            />
          </Faixa>
        )}

        {temChuva && (
          <Faixa
            titulo="Chuva mm/h"
            escala={total48 ? `${numero(total48.mm, 1)} em 48 h` : null}
            leitura={<Valor v={em(serie.chuva)} formato={(x) => `${numero(x, 1)} mm`} />}
          >
            <Barras
              serie={serie.chuva}
              instantes={instantes}
              iAtual={iAtual}
              cursor={cursor}
              rotulo={
                total48 && total48.mm > 0
                  ? `Chuva hora a hora nas últimas 48 horas, pico de ${numero(Math.max(...serie.chuva.filter((x): x is number => x !== null)), 1)} milímetros`
                  : 'Sem chuva medida nas últimas 48 horas'
              }
            />
          </Faixa>
        )}

        {/* O rótulo colado ao cursor sai: "09:00" ao lado da marca das 08:00 se lia como a hora dela. */}
        <EixoTempo instantes={instantes} className="text-[10px]" perto={destaque} />
      </div>

      {/* A previsão deste lugar (o "Próximos dias"). */}
      <ProximosDiasAqui lon={estacao.lon} lat={estacao.lat} />
      <div className="border-t border-white/10 pt-1 text-[11px] text-ink-400">
        {formatLonLat(estacao.lon, estacao.lat)}
        {estacao.alt !== null && <> · {numero(Math.round(estacao.alt))} m</>}
        {' · '}
        <a href={urlDaFonte(estacao)} target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-0.5 text-rio-300 hover:underline">
          {estacao.rede === 'm' ? 'METAR na NOAA' : 'tabela no INMET'}
          <ExternalLink size={10} />
        </a>
        {estacao.wigos && <div className="whitespace-nowrap">WIGOS {estacao.wigos}</div>}
      </div>
    </div>
  );
}
